import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, AlertCircle, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCanvasStore } from "../../stores/canvas.js";
import { useWorldStore } from "../../stores/world.js";
import { useCreateTopology } from "../../api/hooks.js";
import { flowToTopology, validateDefinition } from "../../lib/flow-to-topology.js";

const TOPOLOGY_TYPES = ["custom", "pipeline", "hierarchical", "star", "mesh"];

export function SaveTopologyModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [type, setType] = useState("custom");
  const [errors, setErrors] = useState<string[]>([]);
  const createTopology = useCreateTopology();
  const navigate = useNavigate();
  const setMode = useWorldStore((s) => s.setMode);
  const resetCanvas = useCanvasStore((s) => s.reset);
  const nodeCount = useCanvasStore((s) => s.nodes.length);
  const edgeCount = useCanvasStore((s) => s.edges.length);

  async function handleSave() {
    const { nodes, edges } = useCanvasStore.getState();
    const def = flowToTopology(nodes, edges, type);
    const errs = validateDefinition(def);
    if (errs.length > 0) {
      setErrors(errs);
      return;
    }
    setErrors([]);
    try {
      const result = await createTopology.mutateAsync({ definition: def });
      resetCanvas();
      setMode("monitor");
      onClose();
      navigate(`/topology/${result.id}`);
    } catch (e) {
      setErrors([e instanceof Error ? e.message : "Failed to create topology."]);
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-modal flex items-center justify-center p-6"
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.96, opacity: 0 }}
            transition={{ type: "spring", duration: 0.3, bounce: 0 }}
            className="overlay-panel relative w-[420px] rounded-2xl"
          >
            <div className="flex items-center justify-between border-b border-white/5 px-5 py-4">
              <h2 className="text-sm font-medium text-text-primary">Save topology</h2>
              <button
                onClick={onClose}
                className="p-1 text-text-muted transition-[color,transform] duration-200 hover:text-text-primary active:scale-[0.96]"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4 px-5 py-4">
              <p className="text-xs text-text-muted">
                <span className="tabular-nums text-text-secondary">{nodeCount}</span> agents ·{" "}
                <span className="tabular-nums text-text-secondary">{edgeCount}</span> connections
              </p>

              <div>
                <label className="mb-1.5 block text-xs text-text-muted">Layout type</label>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  className="w-full rounded-lg border border-border-subtle/30 bg-surface/40 px-3 py-2 text-sm capitalize text-text-primary transition-colors duration-200 focus:border-brand-purple/40 focus:outline-none"
                >
                  {TOPOLOGY_TYPES.map((t) => (
                    <option key={t} value={t} className="capitalize">
                      {t}
                    </option>
                  ))}
                </select>
              </div>

              {errors.length > 0 && (
                <div className="space-y-1.5 rounded-lg border border-brand-pink/20 bg-brand-pink/5 p-3">
                  {errors.map((err, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs text-brand-pink">
                      <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <span>{err}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 border-t border-white/5 px-5 py-4">
              <button
                onClick={onClose}
                className="rounded-lg px-3 py-2 text-sm text-text-secondary transition-[color,transform] duration-200 hover:text-text-primary active:scale-[0.96]"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={createTopology.isPending}
                className="flex items-center gap-1.5 rounded-lg bg-brand-purple px-4 py-2 text-sm font-medium text-white transition-[background-color,box-shadow,transform] duration-200 hover:shadow-glow-purple active:scale-[0.96] disabled:opacity-40"
              >
                <Check className="h-4 w-4" />
                {createTopology.isPending ? "Creating…" : "Create topology"}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
